import React from 'react';
import { Modal, Backdrop, Typography, Grid } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import * as yup from 'yup';
import { useFormik } from 'formik';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import DayjsUtils from '@date-io/dayjs';
import dayjs from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import { debounce } from 'lodash';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import ConfirmModal from 'components/atoms/ConfirmModal';
import { useQuery, useInfiniteQuery } from 'react-query';
import { getInstallationFilter } from 'services/installation';
import {
  getMaterialClassOptions,
  getGroupOptions,
  getItemDescriptionOptions,
  getModelOptions,
  getWarehouseId,
  getSupplierOptions,
  postAddStockOptions,
} from 'services/stock-position/index';
import qs from 'querystring';
import { IStockPositionFilterData } from 'interfaces/StockPosition';
import { IModalAddProps, IOption } from './types';
import useStyles from './style';

const FORM_ID = 'ADD_STOCK_POSITION';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const OPTION = 'Opcional';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const MIN_DATE = dayjs('2000-01-01');
const MAX_DATE = dayjs('2100-01-01');

const TAKE = 10;

const mapFilterOptions = (
  data: {
    id: number;
    description: string;
  }[],
) =>
  data.map(item => {
    return {
      label: item?.description,
      value: item?.id,
    };
  }) as IOption[];

const ModalAdd = ({ open, onClose, children, reloadData }: IModalAddProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const enableQueries = React.useMemo(() => open, [open]);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [installationOptions, setInstallationOptions] = React.useState<
    IOption[]
  >([]);
  const [materialClassOptions, setMaterialClassOptions] = React.useState<
    IOption[]
  >([]);
  const [itemDescriptionOptions, setitemDescriptionOptions] = React.useState<
    IOption[]
  >([]);
  const [supplierOptions, setSupplierOptions] = React.useState<IOption[]>([]);
  const [modelOptions, setModelOptions] = React.useState<IOption[]>([]);
  const [groupOptions, setGroupOptions] = React.useState<IOption[]>([]);
  const [wareHouseOptions, setWareHouseOptions] = React.useState<IOption[]>([]);

  const [fieldsSearchText, setFieldsSearchText] = React.useState<{
    [key: string]: string;
  }>({});

  const validationSchema = yup.object({
    installationId: yup.string().required(DEFAULT_OK_TEXT),
    itemDescription: yup.string().required(DEFAULT_OK_TEXT),
    model: yup.string().required(DEFAULT_OK_TEXT),
    provider: yup.string().required(DEFAULT_OK_TEXT),
    materialClassId: yup.string().required(DEFAULT_OK_TEXT),
    groupId: yup.string().required(DEFAULT_OK_TEXT),
    minimumQuantity: yup
      .mixed()
      .required(DEFAULT_OK_TEXT) // eslint-disable-next-line func-names
      .test('is-between', DEFAULT_ERROR_TEXT, function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { maximumQuantity } = this.parent;

        return value >= 0 && value <= maximumQuantity;
      }),
    maximumQuantity: yup
      .mixed()
      .required(DEFAULT_OK_TEXT) // eslint-disable-next-line func-names
      .test('is-between', DEFAULT_ERROR_TEXT, function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { minimumQuantity } = this.parent;

        return value >= minimumQuantity;
      }),
    currentQuantity: yup
      .number()
      .positive(DEFAULT_ERROR_TEXT)
      .required(DEFAULT_OK_TEXT),
    entryDate: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        return dayjs(value).isValid();
      }),
    warehouseId: yup.string().required(DEFAULT_OK_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      installationId: '',
      itemDescription: '',
      model: '',
      provider: '',
      materialClassId: '',
      groupId: '',
      currentQuantity: '',
      minimumQuantity: '',
      maximumQuantity: '',
      entryDate: null,
      warehouseId: '',
      local: '',
      costCenter: '',
      unityValue: 0,
      validity: null,
      leadtime: 0,
      lifespan: 0,
      orderNumber: '',
      oracleCode: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const { ...values } = formValues;
      setLoadingSaveForm(true);

      postAddStockOptions({ ...values })
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  const getNextPageParam = React.useCallback(
    (
      lastPage: IStockPositionFilterData,
      allPages: IStockPositionFilterData[],
    ) => {
      const allResults = allPages.map(page => page.result).flat().length;
      if (allResults === 0) return 1;

      const hasNextPage = allResults < lastPage.count;
      if (hasNextPage) return allResults / TAKE + 1;

      return undefined;
    },
    [],
  );

  const enableInstallationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryInstallationOptions = useQuery({
    queryKey: ['installationOptionsContext'],
    queryFn: async () => {
      const { data } = await getInstallationFilter();
      return data;
    },
    select: data => {
      return mapFilterOptions(data);
    },
    onSuccess: (data: IOption[]) => {
      setInstallationOptions(data);
    },
    enabled: enableInstallationOptions,
    cacheTime: 0,
  });

  const enableSupplierOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const querySupplierOptions = useInfiniteQuery({
    queryKey: ['supplierOptionsContext', [fieldsSearchText?.provider]],
    queryFn: async ({ pageParam = 1 }) => {
      const params = { page: pageParam, take: TAKE } as {
        page: number;
        take: number;
        description?: string;
      };

      if (fieldsSearchText?.provider) {
        params.description = fieldsSearchText?.provider;
      }

      const qsparams = qs.stringify(params);

      const { data } = await getSupplierOptions(qsparams);
      return data;
    },
    getNextPageParam,
    onSuccess: data => {
      const options = data?.pages
        .map(item => item.result)
        .flat()
        .map(item => ({
          label: item.description,
          value: item.description,
        }));
      setSupplierOptions(options);
    },
    enabled: enableSupplierOptions,
    cacheTime: 0,
  });

  const enableItemDescriptionOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryItemDescriptionOptions = useInfiniteQuery({
    queryKey: [
      'itemDescriptionOptionsContext',
      [fieldsSearchText?.itemDescription],
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const params = { page: pageParam, take: TAKE } as {
        page: number;
        take: number;
        description?: string;
      };

      if (fieldsSearchText?.itemDescription) {
        params.description = fieldsSearchText?.itemDescription;
      }

      const qsparams = qs.stringify(params);

      const { data } = await getItemDescriptionOptions(qsparams);
      return data;
    },
    getNextPageParam,
    onSuccess: data => {
      const options = data?.pages
        .map(item => item.result)
        .flat()
        .map(item => ({
          label: item.description,
          value: item.description,
        }));
      setitemDescriptionOptions(options);
    },
    enabled: enableItemDescriptionOptions,
    cacheTime: 0,
  });

  const enableMaterialClassOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );
  const queryMaterialClassOptions = useQuery({
    queryKey: ['materialClassOptionsContext'],
    queryFn: async () => {
      const { data } = await getMaterialClassOptions();
      return data;
    },
    select: data => {
      return mapFilterOptions(data);
    },
    onSuccess: (data: IOption[]) => {
      setMaterialClassOptions(data);
    },
    enabled: enableMaterialClassOptions,
    cacheTime: 0,
  });

  const enableGroupOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );
  const queryGroupOptions = useQuery({
    queryKey: ['groupOptionsContext'],
    queryFn: async () => {
      const { data } = await getGroupOptions();
      return data;
    },
    select: data => {
      return mapFilterOptions(data);
    },
    onSuccess: (data: IOption[]) => {
      setGroupOptions(data);
    },
    enabled: enableGroupOptions,
    cacheTime: 0,
  });

  const enableWareHouseOptions = React.useMemo(
    () => enableQueries && Boolean(formik?.values.installationId),
    [enableQueries, formik?.values.installationId],
  );

  const queryFilterWarehouse = useQuery({
    queryKey: ['warehouseFilterContext', [formik?.values.installationId]],
    queryFn: async () => {
      const id = formik.values?.installationId;
      const { data } = await getWarehouseId(id);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setWareHouseOptions(data);
    },
    enabled: enableWareHouseOptions,
    cacheTime: 0,
  });

  const enableModelOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryModelOptions = useInfiniteQuery({
    queryKey: ['modelOptionsOptionsContext', [fieldsSearchText?.model]],
    queryFn: async ({ pageParam = 1 }) => {
      const params = { page: pageParam, take: TAKE } as {
        page: number;
        take: number;
        description?: string;
      };

      if (fieldsSearchText?.model) {
        params.description = fieldsSearchText?.model;
      }

      const qsparams = qs.stringify(params);

      const { data } = await getModelOptions(qsparams);
      return data;
    },
    getNextPageParam,
    onSuccess: data => {
      const options = data?.pages
        .map(item => item.result)
        .flat()
        .map(item => ({
          label: item.description,
          value: item.description,
        }));
      setModelOptions(options);
    },
    enabled: enableModelOptions,
    cacheTime: 0,
  });

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
    }
  }, [formik, onClose]);

  const onCloseAndClearSearchField = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-types
    (event: React.ChangeEvent<{}>) => {
      const filterName = (event.target as HTMLInputElement).name;
      setFieldsSearchText(currentValues => {
        if (filterName in currentValues) {
          return {
            ...currentValues,
            [filterName]: '',
          };
        }

        return currentValues;
      });
    },
    [],
  );

  const filter = createFilterOptions<IOption>();
  const renderForm = React.useCallback(() => {
    return (
      <form
        id={FORM_ID}
        className={classes.bodyContent}
        onSubmit={formik.handleSubmit}
      >
        <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
          <Grid className={classes.gridContent}>
            <Grid container direction="row" alignItems="center" spacing={2}>
              {/* START ROW 1 */}
              <Grid item xs={4}>
                <Autocomplete
                  id="installationId"
                  className={classes.inputList}
                  options={installationOptions}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      warehouseId: '',
                    }));

                    return handleAutoCompleteChange(
                      'installationId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'installationId',
                      label: 'Instalação',
                      error:
                        formik.touched.installationId &&
                        Boolean(formik.errors.installationId),
                      helperText:
                        (formik.touched.installationId &&
                          formik.errors.installationId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryInstallationOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="itemDescription"
                  className={classes.inputList}
                  options={itemDescriptionOptions}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      itemDescription: '',
                    }));

                    return handleAutoCompleteChange(
                      'itemDescription',
                      selected as IOption,
                    );
                  }}
                  onClose={onCloseAndClearSearchField}
                  getOptionDisabled={option => Boolean(option?.disabled)}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue.length >= 2) {
                      filtered.push({
                        label: params.inputValue,
                        value: params.inputValue,
                      });
                    }

                    if (
                      queryItemDescriptionOptions.isFetching ||
                      queryItemDescriptionOptions.isLoading
                    ) {
                      return [
                        ...filtered,
                        {
                          label: 'Carregando...',
                          value: 'carregando',
                          disabled: true,
                        },
                      ];
                    }

                    return filtered;
                  }}
                  renderInput={params => {
                    const extraProps = {
                      name: 'itemDescription',
                      label: 'Descrição do Item',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.itemDescription &&
                        Boolean(formik.errors.itemDescription),
                      helperText:
                        (formik.touched.itemDescription &&
                          formik.errors.itemDescription) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryItemDescriptionOptions?.isLoading ||
                        queryItemDescriptionOptions?.isFetching,
                    );
                  }}
                  ListboxProps={{
                    onScroll: event => {
                      const listboxNode = event.currentTarget;
                      // Se o número de pixeles desplazados
                      // mais a altura do elemento (em px)
                      // for maior ou igual ao 98% da altura do elemento
                      // e tiver mais págs requisita novamente
                      if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight * 0.98
                      ) {
                        if (
                          queryItemDescriptionOptions?.hasNextPage &&
                          !queryItemDescriptionOptions?.isFetching
                        ) {
                          queryItemDescriptionOptions.fetchNextPage();
                        }
                      }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="model"
                  className={classes.inputList}
                  options={modelOptions}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      model: '',
                    }));
                    return handleAutoCompleteChange(
                      'model',
                      selected as IOption,
                    );
                  }}
                  onClose={onCloseAndClearSearchField}
                  getOptionDisabled={option => Boolean(option?.disabled)}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue.length >= 2) {
                      filtered.push({
                        label: params.inputValue,
                        value: params.inputValue,
                      });
                    }

                    if (
                      queryModelOptions.isFetching ||
                      queryModelOptions.isLoading
                    ) {
                      return [
                        ...filtered,
                        {
                          label: 'Carregando...',
                          value: 'carregando',
                          disabled: true,
                        },
                      ];
                    }

                    return filtered;
                  }}
                  renderInput={params => {
                    const extraProps = {
                      name: 'model',
                      label: 'Modelo',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.model && Boolean(formik.errors.model),
                      helperText:
                        (formik.touched.model && formik.errors.model) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryModelOptions?.isLoading ||
                        queryModelOptions?.isFetching,
                    );
                  }}
                  ListboxProps={{
                    onScroll: event => {
                      const listboxNode = event.currentTarget;
                      // Se o número de pixeles desplazados
                      // mais a altura do elemento (em px)
                      // for maior ou igual ao 98% da altura do elemento
                      // e tiver mais págs requisita novamente
                      if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight * 0.98
                      ) {
                        if (
                          queryModelOptions?.hasNextPage &&
                          !queryModelOptions?.isFetching
                        ) {
                          queryModelOptions.fetchNextPage();
                        }
                      }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="provider"
                  className={classes.inputList}
                  options={supplierOptions}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      provider: '',
                    }));

                    return handleAutoCompleteChange(
                      'provider',
                      selected as IOption,
                    );
                  }}
                  onClose={onCloseAndClearSearchField}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue.length >= 2) {
                      filtered.push({
                        label: params.inputValue,
                        value: params.inputValue,
                      });
                    }

                    if (
                      querySupplierOptions.isFetching ||
                      querySupplierOptions.isLoading
                    ) {
                      return [
                        ...filtered,
                        {
                          label: 'Carregando...',
                          value: 'carregando',
                          disabled: true,
                        },
                      ];
                    }

                    return filtered;
                  }}
                  getOptionDisabled={option => Boolean(option?.disabled)}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      id: 'provider',
                      name: 'provider',
                      label: 'Fornecedor',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.provider &&
                        Boolean(formik.errors.provider),
                      helperText:
                        (formik.touched.provider && formik.errors.provider) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      querySupplierOptions?.isLoading ||
                        querySupplierOptions?.isFetching,
                    );
                  }}
                  ListboxProps={{
                    onScroll: event => {
                      const listboxNode = event.currentTarget;
                      // Se o número de pixeles desplazados
                      // mais a altura do elemento (em px)
                      // for maior ou igual ao 98% da altura do elemento
                      // e tiver mais págs requisita novamente
                      if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight * 0.98
                      ) {
                        if (
                          querySupplierOptions?.hasNextPage &&
                          !querySupplierOptions?.isFetching
                        ) {
                          querySupplierOptions.fetchNextPage();
                        }
                      }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="materialClassId"
                  className={classes.inputList}
                  options={materialClassOptions}
                  value={
                    formik.values.materialClassId
                      ? materialClassOptions.find(item => {
                          return item.value === formik.values.materialClassId;
                        })
                      : null
                  }
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      materialClassId: '',
                    }));

                    return handleAutoCompleteChange(
                      'materialClassId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'materialClassId',
                      label: 'Classe do Material',
                      error:
                        formik.touched.materialClassId &&
                        Boolean(formik.errors.materialClassId),
                      helperText:
                        (formik.touched.materialClassId &&
                          formik.errors.materialClassId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryMaterialClassOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="groupId"
                  className={classes.inputList}
                  options={groupOptions}
                  value={
                    formik.values.groupId
                      ? groupOptions.find(item => {
                          return item.value === formik.values.groupId;
                        })
                      : null
                  }
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      groupId: '',
                    }));
                    return handleAutoCompleteChange(
                      'groupId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'groupId',
                      label: 'Grupo',
                      error:
                        formik.touched.groupId &&
                        Boolean(formik.errors.groupId),
                      helperText:
                        (formik.touched.groupId && formik.errors.groupId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryGroupOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="currentQuantity"
                  name="currentQuantity"
                  label="Qnt. Atual"
                  onBlur={formik.handleBlur}
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.currentQuantity}
                  error={
                    formik.touched.currentQuantity &&
                    Boolean(formik.errors.currentQuantity)
                  }
                  helperText={
                    formik.touched.currentQuantity &&
                    formik.errors.currentQuantity
                      ? DEFAULT_ERROR_TEXT
                      : DEFAULT_OK_TEXT
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="minimumQuantity"
                  name="minimumQuantity"
                  label="Qnt. Mín"
                  variant="outlined"
                  onBlur={formik.handleBlur}
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.minimumQuantity}
                  error={
                    formik.touched.minimumQuantity &&
                    Boolean(formik.errors.minimumQuantity)
                  }
                  helperText={
                    formik.touched.currentQuantity &&
                    formik.errors.currentQuantity
                      ? DEFAULT_ERROR_TEXT
                      : DEFAULT_OK_TEXT
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="maximumQuantity"
                  name="maximumQuantity"
                  label="Qnt. Máx"
                  variant="outlined"
                  onBlur={formik.handleBlur}
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.maximumQuantity}
                  error={
                    formik.touched.maximumQuantity &&
                    Boolean(formik.errors.maximumQuantity)
                  }
                  helperText={
                    formik.touched.currentQuantity &&
                    formik.errors.currentQuantity
                      ? DEFAULT_ERROR_TEXT
                      : DEFAULT_OK_TEXT
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <KeyboardDatePicker
                  id="entryDate"
                  name="entryDate"
                  label="Data de Entrada"
                  inputVariant="outlined"
                  format="DD/MM/YY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  clearable
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  onChange={date => handleDateChange('entryDate', date)}
                  value={formik.values.entryDate}
                  error={
                    formik.touched.entryDate && Boolean(formik.errors.entryDate)
                  }
                  helperText={
                    (formik.touched.entryDate && formik.errors.entryDate) ||
                    DEFAULT_HELPER_TEXT
                  }
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="warehouseId"
                  className={classes.inputList}
                  options={wareHouseOptions}
                  disabled={!formik.values.installationId}
                  value={
                    formik.values.warehouseId
                      ? wareHouseOptions.find(item => {
                          return item.value === formik.values.warehouseId;
                        })
                      : null
                  }
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'warehouseId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'warehouseId',
                      label: 'Almoxarifado',
                      error:
                        formik.touched.warehouseId &&
                        Boolean(formik.errors.warehouseId),
                      helperText:
                        (formik.touched.warehouseId &&
                          formik.errors.warehouseId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryFilterWarehouse?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="local"
                  name="local"
                  label="Local"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.local}
                  error={formik.touched.local && Boolean(formik.errors.local)}
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="costCenter"
                  name="costCenter"
                  label="Centro de custo"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.costCenter}
                  error={
                    formik.touched.costCenter &&
                    Boolean(formik.errors.costCenter)
                  }
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="unityValue"
                  name="unityValue"
                  label="Valor Unit (R$)"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.unityValue}
                  error={
                    formik.touched.unityValue &&
                    Boolean(formik.errors.unityValue)
                  }
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <KeyboardDatePicker
                  id="validity"
                  name="validity"
                  label="Validade"
                  inputVariant="outlined"
                  format="DD/MM/YYYY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  onChange={date => handleDateChange('validity', date)}
                  value={formik.values.validity}
                  error={
                    formik.touched.validity && Boolean(formik.errors.validity)
                  }
                  helperText={
                    (formik.touched.validity && formik.errors.validity) ||
                    OPTION
                  }
                  clearable
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="leadtime"
                  name="leadtime"
                  label="Lead Time"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.leadtime}
                  error={
                    formik.touched.leadtime && Boolean(formik.errors.leadtime)
                  }
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="lifespan"
                  name="lifespan"
                  label="Vida Útil"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                    type: 'number',
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.lifespan}
                  error={
                    formik.touched.lifespan && Boolean(formik.errors.lifespan)
                  }
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="orderNumber"
                  name="orderNumber"
                  label="N° do Pedido"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.orderNumber}
                  error={
                    formik.touched.orderNumber &&
                    Boolean(formik.errors.orderNumber)
                  }
                  helperText={OPTION}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="oracleCode"
                  name="oracleCode"
                  label="Cód. Oracle"
                  variant="outlined"
                  inputProps={{
                    maxLength: 200,
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.oracleCode}
                  error={
                    formik.touched.oracleCode &&
                    Boolean(formik.errors.oracleCode)
                  }
                  helperText={OPTION}
                />
              </Grid>
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.gridContent,
    classes.inputList,
    formik,
    installationOptions,
    itemDescriptionOptions,
    onCloseAndClearSearchField,
    modelOptions,
    supplierOptions,
    materialClassOptions,
    groupOptions,
    wareHouseOptions,
    handleAutoCompleteChange,
    inputRenderer,
    queryInstallationOptions?.isLoading,
    filter,
    queryItemDescriptionOptions,
    handleFieldSearchTextChange,
    queryModelOptions,
    querySupplierOptions,
    queryMaterialClassOptions?.isLoading,
    queryGroupOptions?.isLoading,
    handleDateChange,
    queryFilterWarehouse?.isLoading,
  ]);

  React.useEffect(() => {
    if (!open) {
      if (formik.dirty) {
        formik.resetForm();
      }

      // Se a modal estiver fechada remover a requisição
      queryItemDescriptionOptions?.remove();
      queryModelOptions?.remove();
      querySupplierOptions?.remove();
    }
  }, [
    formik,
    itemDescriptionOptions.length,
    open,
    queryItemDescriptionOptions,
    queryItemDescriptionOptions.remove,
    queryModelOptions,
    querySupplierOptions,
  ]);

  return (
    <>
      {children}
      <ConfirmModal
        title="Confirmar Saída"
        message="É possível que as alterações feitas não sejam salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <Modal
        BackdropComponent={props => <Backdrop {...props} invisible />}
        open={open}
        onClose={onCloseModal}
        className={classes.presentation}
      >
        <div className={classes.modalPresentation}>
          <div className={classes.modalCard}>
            <div className={classes.modalHeader}>
              <div className={classes.modalHeaderItem}>
                <ArrowBackIcon
                  className={classes.headerIcon}
                  onClick={onCloseModal}
                />
                <Typography variant="h3" className={classes.headerTitle}>
                  Adicionar
                </Typography>
              </div>
              <div className={classes.modalHeaderItem}>
                <ButtonWithLoading
                  disabled={!formik.dirty || !formik.isValid}
                  color="primary"
                  variant="contained"
                  form={FORM_ID}
                  type="submit"
                  isLoading={loadingSaveForm}
                >
                  Salvar
                </ButtonWithLoading>
              </div>
            </div>
            <div className={classes.modalBody}>{renderForm()}</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalAdd;
